import { __rest } from "tslib";
import CalendarOutlined from '@haier/icons/CalendarOutlined';
import ClockCircleOutlined from '@haier/icons/ClockCircleOutlined';
import CloseCircleFilled from '@haier/icons/CloseCircleFilled';
import classNames from 'classnames';
import RCPicker from 'rc-picker';
import * as React from 'react';
import { forwardRef, useContext, useImperativeHandle } from 'react';
import { useCompactItemContext } from '../../space/Compact';
import { Components, getTimeProps } from '.';
import { ConfigContext } from '../../config-provider';
import DisabledContext from '../../config-provider/DisabledContext';
import SizeContext from '../../config-provider/SizeContext';
import { FormItemInputContext } from '../../form/context';
import LocaleReceiver from '../../locale-provider/LocaleReceiver';
import useBuriedPoint from '../../_util/hooks/useBuriedPoint';
import { getMergedStatus, getStatusClassNames } from '../../_util/statusUtils';
import warning from '../../_util/warning';
import zhCN from '../locale/zh_CN';
import { getPlaceholder, transPlacement2DropdownAlign } from '../util';
export default function generatePicker(generateConfig) {
    function getPicker(picker, displayName) {
        const Picker = forwardRef((props, ref) => {
            const { prefixCls: customizePrefixCls, getPopupContainer: customizeGetPopupContainer, className, size: customizeSize, bordered = true, placement, placeholder, popupClassName, dropdownClassName, disabled: customDisabled, status: customStatus, borderRadius = 'small' } = props, // 3.4.18
            restProps = __rest(props, ["prefixCls", "getPopupContainer", "className", "size", "bordered", "placement", "placeholder", "popupClassName", "dropdownClassName", "disabled", "status", "borderRadius"]);
            useBuriedPoint('DatePicker');
            const { getPrefixCls, direction, getPopupContainer } = useContext(ConfigContext);
            const prefixCls = getPrefixCls('picker', customizePrefixCls);
            const { compactSize, compactItemClassnames } = useCompactItemContext(prefixCls, direction);
            const innerRef = React.useRef(null);
            const { format, showTime } = props;
            useImperativeHandle(ref, () => ({
                focus: () => { var _a; return (_a = innerRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
                blur: () => { var _a; return (_a = innerRef.current) === null || _a === void 0 ? void 0 : _a.blur(); },
            }));
            const additionalProps = {
                showToday: true,
            };
            let additionalOverrideProps = {};
            if (picker) {
                additionalOverrideProps.picker = picker;
            }
            const mergedPicker = picker || props.picker;
            additionalOverrideProps = Object.assign(Object.assign(Object.assign({}, additionalOverrideProps), (showTime ? getTimeProps(Object.assign({ format, picker: mergedPicker }, showTime)) : {})), (mergedPicker === 'time'
                ? getTimeProps(Object.assign(Object.assign({ format }, props), { picker: mergedPicker }))
                : {}));
            const rootPrefixCls = getPrefixCls();
            // =================== Warning =====================
            warning(picker !== 'quarter', displayName, `DatePicker.${displayName} is legacy usage. Please use DatePicker[picker='${picker}'] directly.`);
            warning(!dropdownClassName, mergedPicker === 'time' ? 'TimePicker' : 'DatePicker', '`dropdownClassName` is deprecated which will be removed in next major version. Please use `popupClassName` instead.');
            // ===================== Size =====================
            const size = React.useContext(SizeContext);
            const mergedSize = compactSize || customizeSize || size;
            // ===================== Disabled =====================
            const disabled = React.useContext(DisabledContext);
            const mergedDisabled = customDisabled !== null && customDisabled !== void 0 ? customDisabled : disabled;
            // ===================== FormItemInput =====================
            const formItemContext = useContext(FormItemInputContext);
            const { hasFeedback, status: contextStatus, feedbackIcon } = formItemContext;
            const suffixNode = (React.createElement(React.Fragment, null,
                mergedPicker === 'time' ? React.createElement(ClockCircleOutlined, null) : React.createElement(CalendarOutlined, null),
                hasFeedback && feedbackIcon));
            return (React.createElement(LocaleReceiver, { componentName: "DatePicker", defaultLocale: zhCN }, (contextLocale) => {
                const locale = Object.assign(Object.assign({}, contextLocale), props.locale);
                return (React.createElement(RCPicker, Object.assign({ ref: innerRef, placeholder: getPlaceholder(mergedPicker, locale, placeholder), suffixIcon: suffixNode, dropdownAlign: transPlacement2DropdownAlign(direction, placement), clearIcon: React.createElement(CloseCircleFilled, null), prevIcon: React.createElement("span", { className: `${prefixCls}-prev-icon` }), nextIcon: React.createElement("span", { className: `${prefixCls}-next-icon` }), superPrevIcon: React.createElement("span", { className: `${prefixCls}-super-prev-icon` }), superNextIcon: React.createElement("span", { className: `${prefixCls}-super-next-icon` }), allowClear: true, transitionName: `${rootPrefixCls}-slide-up` }, additionalProps, restProps, additionalOverrideProps, { locale: locale.lang, className: classNames({
                        [`${prefixCls}-${mergedSize}`]: mergedSize,
                        [`${prefixCls}-borderless`]: !bordered,
                        [`${prefixCls}-radius-lg`]: borderRadius === 'large',
                        [`${prefixCls}-radius-md`]: borderRadius === 'middle',
                    }, getStatusClassNames(prefixCls, getMergedStatus(contextStatus, customStatus), hasFeedback), compactItemClassnames, className), prefixCls: prefixCls, getPopupContainer: customizeGetPopupContainer || getPopupContainer, generateConfig: generateConfig, components: Components, direction: direction, disabled: mergedDisabled, dropdownClassName: popupClassName || dropdownClassName })));
            }));
        });
        if (displayName) {
            Picker.displayName = displayName;
        }
        return Picker;
    }
    const DatePicker = getPicker();
    const WeekPicker = getPicker('week', 'WeekPicker');
    const MonthPicker = getPicker('month', 'MonthPicker');
    const YearPicker = getPicker('year', 'YearPicker');
    /* @ts-ignore */
    const TimePicker = getPicker('time', 'TimePicker');
    /* @ts-ignore */
    const QuarterPicker = getPicker('quarter', 'QuarterPicker');
    return { DatePicker, WeekPicker, MonthPicker, YearPicker, TimePicker, QuarterPicker };
}
